/* [04]-STUDIOS™ — Local cart memory, V6 (rev 2026-10-29).
   Keeps only a recent snapshot on this browser. No automatic cart changes.
   Shopify remains the source of truth for live stock and checkout prices.
   Containers with [data-cart04-memory-actions] (empty cart page) get per-item
   "Add to cart" buttons and a "Clear all" control. Other containers (drawer)
   keep the compact list with "Restore saved items", which refreshes the drawer
   in place instead of leaving the page.
   rev 2026-10-29: the snapshot is offered only when it makes sense:
   - the customer emptied this same cart themselves -> not offered;
   - a new cart within 7 days (checkout completed) -> not offered (no re-buying);
   - a new cart after 7+ days (cart expired) -> offered, as intended.
   A drawer refresh that does not answer within 4s falls back to the cart page,
   so the button can never stay on "Restoring…". */
(() => {
  'use strict';
  if (window.__cart04MemoryLoaded) return;
  window.__cart04MemoryLoaded = true;

  const KEY = '04-studios:local-cart-memory:v1';
  const MAX_ITEMS = 8;
  const MAX_AGE = 90 * 24 * 60 * 60 * 1000;
  const PURCHASE_WINDOW = 7 * 24 * 60 * 60 * 1000;
  const noop = () => {};
  const root = () => (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const tokenOf = (cart) => String(cart?.token || '').split('?')[0];
  const CART_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M3 4h2.2l2.1 10.2a1.5 1.5 0 0 0 1.5 1.2h8.1a1.5 1.5 0 0 0 1.5-1.1L20 8H6.2"/><circle cx="9.5" cy="19.5" r="1.2"/><circle cx="17" cy="19.5" r="1.2"/></svg>';

  function validSavedItem(item) {
    return item && Number.isSafeInteger(Number(item.variantId)) && Number(item.variantId) > 0 &&
      typeof item.title === 'string' && item.title.length < 320 &&
      Number.isFinite(Number(item.quantity)) && Number(item.quantity) >= 1;
  }
  function readData() {
    try {
      const data = JSON.parse(localStorage.getItem(KEY) || 'null');
      if (!data || !Array.isArray(data.items) || !Number.isFinite(data.savedAt) ||
          Date.now() - data.savedAt > MAX_AGE || data.savedAt > Date.now() + 3600000) return null;
      return data;
    } catch { return null; }
  }
  function readSaved() {
    const data = readData();
    return data ? data.items.filter(validSavedItem).slice(0, MAX_ITEMS) : [];
  }
  function clearSaved() {
    try { localStorage.removeItem(KEY); } catch {}
  }
  function remember(cart) {
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return;
    const items = cart.items.filter(item =>
      item && Number(item.variant_id) > 0 && typeof item.product_title === 'string'
    ).slice(0, MAX_ITEMS).map(item => ({
      variantId: Number(item.variant_id),
      quantity: Math.max(1, Math.min(20, Number(item.quantity) || 1)),
      title: item.product_title.slice(0, 260),
      variant: typeof item.variant_title === 'string' && item.variant_title !== 'Default Title'
        ? item.variant_title.slice(0, 100) : '',
      url: typeof item.url === 'string' && item.url.startsWith('/products/') ? item.url : '',
      image: typeof item.image === 'string' && (
        item.image.startsWith('https://cdn.shopify.com/') ||
        item.image.startsWith('//cdn.shopify.com/') ||
        item.image.startsWith('/cdn/')
      ) ? item.image : ''
    }));
    if (!items.length) return;
    try { localStorage.setItem(KEY, JSON.stringify({savedAt:Date.now(),token:tokenOf(cart),items})); } catch {}
  }
  // The cart is empty: decide whether the saved snapshot is still worth offering.
  function reviewEmptyCart(cart) {
    const data = readData();
    if (!data) return;
    const sameCart = !!data.token && data.token === tokenOf(cart);
    const recent = Date.now() - data.savedAt < PURCHASE_WINDOW;
    if (sameCart || recent) clearSaved();
  }

  function create(tag, className, text) {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text !== undefined) el.textContent = text;
    return el;
  }
  function sized(url, width) {
    try {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('width', String(width));
      return u.href;
    } catch { return url; }
  }
  function detailText(item) {
    return [item.variant, 'Qty ' + item.quantity].filter(Boolean).join(' · ');
  }

  function compactRow(item) {
    const li = create('li','cart04-memory__item');
    if (item.image) {
      const img = create('img','cart04-memory__item-image');
      img.src = item.image;
      img.loading = 'lazy';
      img.alt = '';
      li.append(img);
    } else li.append(create('span','cart04-memory__item-placeholder'));
    const copy = create('div','cart04-memory__item-copy');
    const name = create(item.url ? 'a' : 'span','cart04-memory__item-name',item.title);
    if (item.url) name.href = item.url;
    copy.append(name);
    copy.append(create('div','cart04-memory__item-detail',detailText(item)));
    li.append(copy);
    return li;
  }
  function actionRow(item, index) {
    const li = create('li','c04e-mem__row');
    const thumb = create(item.url ? 'a' : 'span','c04e-mem__thumb');
    if (item.url) {
      thumb.href = item.url;
      thumb.tabIndex = -1;
      thumb.setAttribute('aria-hidden','true');
    }
    if (item.image) {
      const img = create('img');
      img.src = sized(item.image, 260);
      img.loading = 'lazy';
      img.alt = '';
      img.width = 116;
      img.height = 108;
      thumb.append(img);
    }
    li.append(thumb);
    const copy = create('div','c04e-mem__copy');
    const name = create(item.url ? 'a' : 'span','c04e-mem__name',item.title);
    if (item.url) name.href = item.url;
    copy.append(name);
    copy.append(create('p','c04e-mem__detail',detailText(item)));
    li.append(copy);
    const button = create('button','c04e-mem__add');
    button.type = 'button';
    button.dataset.cart04MemoryAdd = String(index);
    button.setAttribute('aria-label','Add ' + item.title + (item.variant ? ', ' + item.variant : '') + ' to cart');
    button.innerHTML = CART_ICON;
    button.append(create('span','c04e-mem__add-label','Add to cart'));
    li.append(button);
    return li;
  }

  function renderSaved() {
    const items = readSaved();
    const signature = JSON.stringify(items);
    for (const container of document.querySelectorAll('[data-cart04-memory]')) {
      if (container.dataset.cart04Render === signature) continue;
      container.dataset.cart04Render = signature;
      container.hidden = items.length === 0;
      const list = container.querySelector('[data-cart04-memory-list]');
      if (!list || !items.length) continue;
      const actions = container.hasAttribute('data-cart04-memory-actions');
      list.replaceChildren();
      items.forEach((item, index) => list.append(actions ? actionRow(item, index) : compactRow(item)));
    }
  }

  let refreshTimeout = 0;
  function refreshSoon(delay = 180) {
    clearTimeout(refreshTimeout);
    refreshTimeout = setTimeout(refreshCart, delay);
  }
  let pendingFetch = null;
  async function refreshCart() {
    if (pendingFetch) return pendingFetch;
    pendingFetch = (async () => {
      try {
        const response = await fetch(root() + 'cart.js', {
          credentials:'same-origin', cache:'no-store', headers:{Accept:'application/json'}
        });
        if (!response.ok) return;
        const cart = await response.json();
        if ((cart?.item_count || 0) > 0) remember(cart);
        else reviewEmptyCart(cart);
      } catch {}
      finally { renderSaved(); pendingFetch = null; }
    })();
    return pendingFetch;
  }

  function goToCart() {
    // Preserve a draft theme preview when a merchant tests recovery.
    const cartUrl = new URL(root() + 'cart', window.location.origin);
    const previewTheme = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (previewTheme && /^\d{8,20}$/.test(previewTheme)) {
      cartUrl.searchParams.set('preview_theme_id', previewTheme);
    }
    window.location.assign(cartUrl.pathname + cartUrl.search);
  }
  function addLine(item) {
    return fetch(root() + 'cart/add.js', {
      method:'POST', credentials:'same-origin',
      headers:{'Content-Type':'application/json',Accept:'application/json'},
      body:JSON.stringify({items:[{id:item.variantId,quantity:item.quantity}]})
    });
  }

  // Inside the cart drawer: re-render the drawer in place through the theme's own
  // cart event, so the customer stays where they are instead of landing on /cart.
  async function refreshDrawer(source, itemCount) {
    try {
      const {CartLinesUpdateEvent} = await import('@shopify/events');
      const ids = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((node) => node.dataset.sectionId).filter(Boolean))].slice(0, 5);
      if (!ids.length) return false;
      const deferred = CartLinesUpdateEvent.createPromise();
      source.dispatchEvent(new CartLinesUpdateEvent({
        action:'add', context:'product', lines:[], promise:deferred.promise
      }));
      try {
        const sectionsUrl = new URL(window.location.pathname, window.location.origin);
        sectionsUrl.searchParams.set('sections', ids.join(','));
        const [sectionsResponse, cartResponse] = await Promise.all([
          fetch(sectionsUrl.pathname + sectionsUrl.search, {credentials:'same-origin', cache:'no-store'}),
          fetch(root() + 'cart.js', {credentials:'same-origin', cache:'no-store'})
        ]);
        if (!sectionsResponse.ok || !cartResponse.ok) throw new Error('Refresh failed');
        const [sections, cart] = [await sectionsResponse.json(), await cartResponse.json()];
        remember(cart);
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {items: cart.items, sections, source: 'product-form-component',
            sourceId: 'cart04-memory', itemCount, didError: false}
        });
        return true;
      } catch (error) {
        deferred.reject(error);
        return false;
      }
    } catch { return false; }
  }
  const withTimeout = (promise, ms) => Promise.race([
    promise, new Promise((resolve) => setTimeout(() => resolve(false), ms))
  ]);

  let busy = false;
  async function restore(button) {
    if (busy) return;
    const box = button.closest('[data-cart04-memory]');
    const status = box?.querySelector('[data-cart04-memory-status]');
    const items = readSaved();
    if (!items.length) { renderSaved(); return; }
    const inDrawer = Boolean(button.closest('#cart-drawer'));
    busy = true;
    button.disabled = true;
    const restoreLabel = button.querySelector('[data-cart04-memory-restore-label]');
    if (restoreLabel) restoreLabel.textContent = 'Restoring…';
    if (status) status.textContent = '';
    let restored = 0, failed = 0;
    try {
      // Work one line at a time so an unavailable product cannot block others.
      for (const item of items) {
        try {
          const response = await addLine(item);
          if (response.ok) restored++; else failed++;
        } catch { failed++; }
      }
      if (restored) {
        if (inDrawer && await withTimeout(refreshDrawer(button, restored), 4000)) return;
        if (status) status.textContent = failed
          ? 'Some items were unavailable. Opening your updated cart…'
          : 'Items restored. Opening your cart…';
        await withTimeout(refreshCart(), 3000);
        goToCart();
      } else if (status) {
        status.textContent = 'Those items could not be restored. They may be sold out or no longer available.';
      }
    } finally {
      busy = false;
      if (button.isConnected) {
        button.disabled = false;
        if (restoreLabel) restoreLabel.textContent = 'Restore saved items';
      }
    }
  }

  async function addOne(button) {
    if (busy) return;
    const item = readSaved()[Number(button.dataset.cart04MemoryAdd)];
    const box = button.closest('[data-cart04-memory]');
    const status = box?.querySelector('[data-cart04-memory-status]');
    if (!item) { renderSaved(); return; }
    const label = button.querySelector('.c04e-mem__add-label');
    busy = true;
    button.disabled = true;
    if (label) label.textContent = 'Adding…';
    if (status) status.textContent = '';
    try {
      const response = await addLine(item);
      if (!response.ok) throw new Error('add failed');
      if (label) label.textContent = 'Added';
      if (status) status.textContent = item.title + ' added. Opening your cart…';
      goToCart();
    } catch {
      if (status) status.textContent = 'This item could not be added. It may be sold out or no longer available.';
      if (label) label.textContent = 'Add to cart';
      button.disabled = false;
    } finally {
      busy = false;
    }
  }

  function clearAll() {
    clearSaved();
    renderSaved();
    const heading = document.querySelector('.c04e__title');
    if (heading instanceof HTMLElement) heading.focus({preventScroll:true});
  }

  function onClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const addButton = target.closest('[data-cart04-memory-add]');
    if (addButton) {
      event.preventDefault();
      addOne(addButton);
      return;
    }
    if (target.closest('[data-cart04-memory-clear]')) {
      event.preventDefault();
      clearAll();
      return;
    }
    const button = target.closest('[data-cart04-memory-restore]');
    if (button) {
      event.preventDefault();
      restore(button);
      return;
    }
    // Cross-app/cart quick-add fallback: persist once Shopify's AJAX
    // action has had time to finish if a connector omitted the cart event.
    if (target.closest('[name="add"],[data-pdpref-buy],[data-add-to-cart],[data-quick-add]')) {
      refreshSoon(1800);
    }
  }
  function onCartUpdate(event) {
    if (event?.promise && typeof event.promise.then === 'function') {
      event.promise.then(() => refreshSoon(100), noop);
    } else refreshSoon(700);
  }
  function start() {
    renderSaved();
    document.addEventListener('click', onClick);
    document.addEventListener('cart:updated', () => refreshSoon(300));
    document.addEventListener('cart:refresh', () => refreshSoon(300));
    document.addEventListener('cart:change', () => refreshSoon(300));
    document.addEventListener('cart:updated-04', () => refreshSoon(300));
    document.addEventListener('cart:update', () => refreshSoon(300));
    document.addEventListener('cart:lines-update', onCartUpdate);
    document.addEventListener('cart:lines:update', onCartUpdate);
    document.addEventListener('cart:line-items:updated', onCartUpdate);
    document.addEventListener('cartLinesUpdate', onCartUpdate);
    window.addEventListener('pageshow', () => { renderSaved();refreshSoon(250); });
    window.addEventListener('storage', (event) => { if(event.key === KEY) renderSaved(); });
    // Observe only cart surfaces. Other page updates must not trigger cart-memory rendering.
    // Keep /cart observing MainContent, because Shopify can replace the entire cart section.
    // A change of the drawer (item removed, drawer emptied) also re-checks the cart.
    let renderFrame = 0;
    const observer = new MutationObserver(() => {
      if (document.hidden || renderFrame) return;
      renderFrame = requestAnimationFrame(() => {
        renderFrame = 0;
        renderSaved();
        refreshSoon(400);
      });
    });
    const drawer = document.getElementById('shopify-section-cart-drawer-section');
    if (drawer) observer.observe(drawer, {subtree:true,childList:true});
    if (window.location.pathname === '/cart' || window.location.pathname === '/cart/') {
      const cartPage = document.getElementById('MainContent');
      if (cartPage) observer.observe(cartPage, {subtree:true,childList:true});
    }
    // Delay network access until the shop has rendered its critical content.
    if ('requestIdleCallback' in window) window.requestIdleCallback(() => refreshSoon(0),{timeout:1600});
    else refreshSoon(1100);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',start,{once:true});
  else start();
})();
